import { useMemo, useState } from "react";
import { isPrebuiltWatchlist } from "../prebuiltWatchlists";
import {
  ListPanel,
  ListRow,
  EmptyNote,
} from "./ListParts";
import {
  portfolioEntry,
  tagWatchlistEntry,
  combinedEntry,
  byName,
  matches,
  type ListEntry,
} from "../listEntries";
import { useTagsWatchlistsCRUD } from "@/shared/api/pref-storage/tags-watchlists/useTagsWatchlistsCRUD";
import { useHeadlineQueriesCRUD } from "@/widget-components/newsquawk-headlines/api/headline-query/useHeadlineQueriesCRUD";
import { useIsDeskUser } from "@/hooks/useIsDeskUser";
import { useListPreview } from "../hooks/useListPreview";
import type { Portfolio } from "@/types/Portfolio";
import type { EditableWatchlist } from "../hooks/useWatchlistCreator";

interface NewsquawkLibraryListProps {
  portfolios: Portfolio[];
  /** Open an editable copy in the builder; nothing is saved until Save. */
  onBuildOn: (item: EditableWatchlist) => void;
  /** Desk only: open the library record itself. */
  onEdit: (item: EditableWatchlist) => void;
}

/**
 * The lists the desk maintains, beside your own.
 *
 * "Start from one of the Newsquawk lists" was promised on the build card but
 * lived behind a toggle to a different page with a different interaction
 * model. Here it is the other half of the same view, in the same rows as the
 * widget menus' "Newsquawk" group — flat and alphabetical, narrowed by its own
 * search box, as the menus and the old library both chose over folders.
 *
 * A click previews a list's headlines below, which is how you judge one of
 * these before taking it: browse the library by what it catches. The copy
 * icon opens an editable copy in the builder — a copy is what you do with
 * someone else's list here — and desk users, who maintain these, get a pencil
 * and the record itself instead.
 *
 * Single topic tags are left out: they are what lists are made of, not lists,
 * and the builder offers them where they are used.
 */
export default function NewsquawkLibraryList({
  portfolios,
  onBuildOn,
  onEdit,
}: NewsquawkLibraryListProps) {
  const isDeskUser = useIsDeskUser();
  const preview = useListPreview();
  const { watchlistsQuery } = useTagsWatchlistsCRUD();
  const { queriesQuery } = useHeadlineQueriesCRUD();
  const tagWatchlists = watchlistsQuery.data;
  const combinedWatchlists = queriesQuery.data;

  const rows = useMemo(() => {
    const out: ListEntry[] = [];
    for (const p of portfolios) {
      // Negative ids are the guided demo's stand-in (demoWatchlist.ts), which
      // is not a library list.
      if (isPrebuiltWatchlist(p) && p.id >= 0) out.push(portfolioEntry(p));
    }
    // Everything not the user's, as the old library had it: EXTERNAL lists
    // are shared with them, not theirs to edit.
    for (const w of tagWatchlists ?? []) {
      if (w.owner_type !== "USER") out.push(tagWatchlistEntry(w));
    }
    for (const q of combinedWatchlists ?? []) {
      if (q.owner_type === "NEWSQUAWK" && q.id !== undefined) {
        out.push(combinedEntry({ ...q, id: q.id }));
      }
    }
    return out.sort(byName);
  }, [portfolios, tagWatchlists, combinedWatchlists]);

  const [search, setSearch] = useState("");
  const query = search.trim().toLowerCase();
  const visible = rows.filter((r) => matches(r, query));
  const loading = watchlistsQuery.isLoading || queriesQuery.isLoading;

  return (
    <ListPanel
      title="Newsquawk library"
      count={rows.length}
      search={
        rows.length > 0
          ? {
              value: search,
              onChange: setSearch,
              placeholder: "Search the library...",
            }
          : undefined
      }
    >
      {rows.length === 0 ? (
        <EmptyNote>
          {loading ? "Loading the library…" : "No library lists to show."}
        </EmptyNote>
      ) : visible.length === 0 ? (
        <EmptyNote>Nothing in the library matches &quot;{search.trim()}&quot;</EmptyNote>
      ) : (
        visible.map((row) => (
          <ListRow
            key={row.key}
            entry={row}
            edit={
              isDeskUser
                ? { action: "Edit", onClick: () => onEdit(row.item) }
                : { action: "Build on", onClick: () => onBuildOn(row.item) }
            }
            onPreview={() => preview.togglePreview(row.item)}
            previewing={preview.isPreviewing(row.item)}
          />
        ))
      )}
    </ListPanel>
  );
}
